import { useAuthStore } from '@/stores/authStore.js'
import { useUserStore } from '@/stores/userStore.js'
import { useApiResource } from '@/composables/resources/useApiResource.js'
import { NO_QUEUE_HEADER } from '@/helpers/offlineSupport.js'

export const useDeviceIdentification = () => {

  // A device has one identifier per agent and partner, kept in IndexedDB, and the server knows a
  // device only for the partner it was provisioned for. So the identifier is read for the partner
  // now selected before deciding whether one is needed: reading it from the store's current value
  // took the previous partner's, because selecting a partner only starts that read (authStore's
  // watcher), and wrote it under the new partner -- a device the server rejects, so every
  // background sync it queued was thrown away.
  async function identifyDevice() {
    const authStore = useAuthStore()
    const userStore = useUserStore()

    if (!userStore.user || !userStore.partner) return

    await authStore.loadDeviceIdentifier()
    if (authStore.hasDeviceIdentifier()) return

    await provisionDevice()
  }

  // Provisions a new device for the agent and the partner selected, replacing the identifier the
  // server no longer knows (it was provisioned for another partner, or expired for inactivity).
  // Resolves with whether it did.
  async function reprovisionDevice() {
    const userStore = useUserStore()
    if (!userStore.user || !userStore.partner) return false

    return provisionDevice()
  }

  // Not offline: the service worker would queue the request and provision the device later, under
  // an identifier this page never hears of. The next identifyDevice online provisions it instead.
  // For the same reason the request is never queued (NO_QUEUE_HEADER) on a signal too weak for it.
  async function provisionDevice() {
    if (navigator.onLine === false) return false

    const authStore = useAuthStore()
    const deviceIdentifier = await _provisionRemoteDevice()
    if (deviceIdentifier) await authStore.setDeviceIdentifier(deviceIdentifier)
    return Boolean(deviceIdentifier)
  }

  async function _provisionRemoteDevice() {
    const userStore = useUserStore()
    const apiResource = useApiResource()
    let response = await apiResource.post("/remote_devices", { partner_id: userStore.partner.id }, { [NO_QUEUE_HEADER]: '1' })

    return response?.data?.uuid
  }

  return {
    identifyDevice, reprovisionDevice
  }
}
